import React, { useState, useMemo } from 'react';
import { useAppSelector, useAppDispatch, store } from '@/store';
import {
    selectAvailableMerchants,
    selectMerchantId,
    selectIsSuperAdmin,
    selectIsImpersonating,
    switchContext,
    initializeAuth,
} from '@/store/slices/authSlice';
import { useStoreData } from '@/hooks/useStoreData';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/features/admin/api';
import {
    ChevronDown,
    Loader2,
    Check,
    Search,
    Lock,
} from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { setImpersonationRedirectHint } from '@/utils/impersonationHint';

/* ─── Helpers ────────────────────────────────────────────────── */

const WORKSPACE_PALETTES = [
    { bg: '#EDE9FE', text: '#7C3AED' },
    { bg: '#DBEAFE', text: '#2563EB' },
    { bg: '#D1FAE5', text: '#059669' },
    { bg: '#FEF3C7', text: '#D97706' },
    { bg: '#FCE7F3', text: '#DB2777' },
    { bg: '#E0E7FF', text: '#4F46E5' },
    { bg: '#CFFAFE', text: '#0891B2' },
    { bg: '#FEE2E2', text: '#DC2626' },
];

const getPalette = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return WORKSPACE_PALETTES[Math.abs(hash) % WORKSPACE_PALETTES.length];
};

const getInitials = (name: string) =>
    name
        .split(/[\s\-_]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('');

/* ─── Component ──────────────────────────────────────────────── */

const WorkspaceSwitcher: React.FC = () => {
    const dispatch = useAppDispatch();
    const reduxMerchants = useAppSelector(selectAvailableMerchants);
    const currentMerchantId = useAppSelector(selectMerchantId);
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
    const isImpersonating = useAppSelector(selectIsImpersonating);
    const { toast } = useToast();
    const { data: storeData } = useStoreData();

    const [isOpen, setIsOpen] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [search, setSearch] = useState('');

    // For super admins, fetch ALL merchants from admin API
    const { data: adminMerchantsData } = useQuery({
        queryKey: ['admin-merchants-switcher'],
        queryFn: () => adminApi.listMerchants({ page: 1, limit: 100 }),
        enabled: isSuperAdmin,
        staleTime: 60_000,
    });

    // Build unified merchant list: admin API for super admins, Redux for others
    const merchants = useMemo(() => {
        if (isSuperAdmin && adminMerchantsData?.merchants?.length) {
            return adminMerchantsData.merchants.map((m) => ({
                merchantId: m.merchantId,
                businessName: m.businessName,
                businessEmail: m.businessEmail,
                status: m.status,
                stores: m.stores || [],
            }));
        }
        return reduxMerchants;
    }, [isSuperAdmin, adminMerchantsData, reduxMerchants]);

    // Only render when user belongs to 2+ workspaces (or is a super admin / impersonating)
    if (merchants.length < 2 && !isSuperAdmin && !isImpersonating) {
        return null;
    }
    if (merchants.length === 0 && !isImpersonating) {
        return null;
    }

    const currentMerchant = merchants.find((m) => m.merchantId === currentMerchantId);
    const workspaceName = currentMerchant?.businessName
        || storeData?.store?.merchantDetails?.businessName
        || (currentMerchantId ? 'Workspace' : 'Select workspace');

    // Filter merchants by search
    const filteredMerchants = search
        ? merchants.filter(
            (m) =>
                m.businessName?.toLowerCase().includes(search.toLowerCase()) ||
                m.businessEmail?.toLowerCase().includes(search.toLowerCase())
          )
        : merchants;

    const handleSwitch = async (merchantId: string) => {
        if (merchantId === currentMerchantId) {
            setIsOpen(false);
            return;
        }

        // Prevent switching to blocked workspaces
        const target = merchants.find((m) => m.merchantId === merchantId);
        if ((target as any)?.blocked) {
            toast({
                title: 'Workspace unavailable',
                description: 'This feature is not enabled for this organization. Contact support.',
                variant: 'destructive',
            });
            return;
        }

        setSwitching(true);
        try {
            // For super admins, use impersonate API to switch to a different merchant
            if (isSuperAdmin) {
                // Fetch merchant detail to get its stores (list API may not include them)
                const merchantDetail = await adminApi.getMerchant(merchantId);
                const targetStore = merchantDetail?.stores?.[0];
                if (!targetStore) {
                    toast({
                        title: 'No stores',
                        description: 'This merchant has no stores connected yet',
                        variant: 'destructive',
                    });
                    setSwitching(false);
                    return;
                }

                // Save context for display
                localStorage.setItem('impersonation_context', JSON.stringify({
                    merchant: {
                        merchantId: merchantDetail.merchantId,
                        businessName: merchantDetail.businessName,
                        businessEmail: merchantDetail.businessEmail,
                        status: merchantDetail.status,
                    },
                    store: {
                        storeId: targetStore.storeId,
                        merchantId,
                        storeName: targetStore.storeName,
                        storeUrl: targetStore.storeUrl,
                        status: targetStore.status,
                        platform: targetStore.platform,
                    },
                }));

                await adminApi.impersonate({ merchantId, storeId: targetStore.storeId });
                await dispatch(initializeAuth()).unwrap();
                setIsOpen(false);
                setImpersonationRedirectHint();
                window.location.href = '/';
            } else {
                // Regular user switch
                const availableStores = store.getState().auth.availableContexts.stores;
                const currentStoreId = store.getState().auth.context.storeId;
                const storeForMerchant = availableStores.find((s) => s.merchantId === merchantId);
                const storeId = storeForMerchant?.storeId || currentStoreId || undefined;

                await dispatch(switchContext({ merchantId, storeId })).unwrap();
                setIsOpen(false);
                window.location.href = '/';
            }
        } catch (error: any) {
            console.error('Failed to switch workspace:', error);
            toast({
                title: 'Operation Failed',
                description: typeof error === 'string' ? error : error?.message || 'Failed to switch workspace',
                variant: 'destructive',
            });
        } finally {
            setSwitching(false);
        }
    };

    const palette = workspaceName && workspaceName !== 'Select workspace'
        ? getPalette(workspaceName)
        : WORKSPACE_PALETTES[0];

    return (
        <div className="px-3 py-2">
            <Popover
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open);
                    if (!open) setSearch('');
                }}
            >
                <PopoverTrigger asChild>
                    <button
                        className="group w-full flex items-center gap-2.5 px-3 py-2.5
                            border border-gray-200 rounded-xl
                            hover:border-gray-300 hover:shadow-sm
                            active:scale-[.98]
                            transition-all duration-200"
                        disabled={switching}
                    >
                        {/* Workspace avatar */}
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                                text-[12px] font-[700] tracking-tight transition-transform duration-200
                                group-hover:scale-105"
                            style={{
                                backgroundColor: palette.bg,
                                color: palette.text,
                            }}
                        >
                            {getInitials(workspaceName || '?')}
                        </div>

                        {/* Name */}
                        <div className="flex-1 min-w-0 text-left">
                            <p className="text-[11px] text-gray-400 leading-tight">
                                Workspace
                            </p>
                            <p className="text-[13px] font-[600] text-[#303030] truncate leading-tight">
                                {workspaceName}
                            </p>
                        </div>

                        {/* Chevron / Loader */}
                        {switching ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400 flex-shrink-0" />
                        ) : (
                            <ChevronDown
                                className="w-4 h-4 text-gray-300 flex-shrink-0
                                    group-hover:text-gray-500 transition-colors duration-200"
                            />
                        )}
                    </button>
                </PopoverTrigger>

                <PopoverContent
                    className="w-[280px] p-0 rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                    align="start"
                    side="top"
                    sideOffset={6}
                >
                    {/* Header */}
                    <div className="px-3.5 pt-3 pb-2">
                        <p className="text-[11px] font-[600] text-gray-400 uppercase tracking-wider">
                            Switch Workspace
                        </p>
                    </div>

                    {/* Search (show for super admins with multiple merchants) */}
                    {merchants.length > 5 && (
                        <div className="px-3 pb-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search merchants…"
                                    className="w-full pl-8 pr-3 py-2 text-[12px] bg-gray-50 border border-gray-200 rounded-lg
                                        placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#303030]/10
                                        transition-all duration-200"
                                />
                            </div>
                        </div>
                    )}

                    {/* Workspace list */}
                    <div className="max-h-[280px] overflow-y-auto px-1.5 pb-1.5 custom-scrollbar">
                        {filteredMerchants.length === 0 ? (
                            <div className="py-6 text-center text-[12px] text-gray-400">
                                No merchants found
                            </div>
                        ) : (
                            filteredMerchants.map((merchant) => {
                                const isActive = merchant.merchantId === currentMerchantId;
                                const isBlocked = (merchant as any).blocked === true;
                                const mp = getPalette(merchant.businessName || merchant.merchantId);
                                return (
                                    <button
                                        key={merchant.merchantId}
                                        onClick={() => !isBlocked && handleSwitch(merchant.merchantId)}
                                        disabled={isBlocked}
                                        className={`group/item w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg
                                            transition-all duration-150 ${isBlocked
                                                ? 'opacity-75 cursor-not-allowed'
                                                : isActive
                                                    ? 'bg-[#F5F5F5]'
                                                    : 'hover:bg-[#FAFAFA]'
                                            }`}
                                    >
                                        {/* Miniature avatar */}
                                        <div
                                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                                                text-[11px] font-[700] tracking-tight"
                                            style={{
                                                backgroundColor: isBlocked ? '#F5F5F5' : mp.bg,
                                                color: isBlocked ? '#9e9e9e' : mp.text,
                                            }}
                                        >
                                            {isBlocked
                                                ? <Lock className="w-3.5 h-3.5 text-[#9e9e9e]" />
                                                : getInitials(merchant.businessName || '?')
                                            }
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0 text-left">
                                            <p
                                                className={`text-[13px] truncate leading-tight ${isBlocked
                                                    ? 'font-[500] text-[#757575]'
                                                    : isActive
                                                        ? 'font-[650] text-[#303030]'
                                                        : 'font-[500] text-gray-600 group-hover/item:text-[#303030]'
                                                    }`}
                                            >
                                                {merchant.businessName || 'Unnamed'}
                                            </p>
                                            {isBlocked ? (
                                                <p className="text-[10px] text-[#E65100] font-[600] leading-tight mt-0.5">
                                                    Feature not enabled
                                                </p>
                                            ) : merchant.businessEmail && (
                                                <p className="text-[11px] text-gray-400 truncate leading-tight mt-0.5">
                                                    {merchant.businessEmail}
                                                </p>
                                            )}
                                        </div>

                                        {/* Active indicator */}
                                        {isActive && !isBlocked && (
                                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={2.5} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default WorkspaceSwitcher;
